import React, { useEffect, useRef, useState } from "react";
import { motion, useScroll, useTransform, useInView, animate } from "framer-motion";
import { Link } from "react-router-dom";
import {
  ArrowRight, ShieldCheck, Building2, Clock, ClipboardList, HardHat, Users, MapPin, Handshake,
  Eye, Gem, RefreshCcw, LifeBuoy, Home as HomeIcon, Globe2, Star, Quote, MessageCircle, CheckCircle2,
} from "lucide-react";
import { api } from "@/lib/api";
import { useSeo } from "@/lib/seo";
import { Reveal } from "@/components/Reveal";
import { StatusBadge } from "@/components/StatusBadge";
import { BRAND } from "@/data/site";
import { ICON_MAP } from "@/data/highlightIcons";

const HERO = "https://images.unsplash.com/photo-1530877872966-40bb5529f558?crop=entropy&cs=srgb&fm=jpg&q=85&w=1920";
const INTRO = "https://images.unsplash.com/photo-1619218070141-bcfeb8b93074?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200";
const INTRO_SMALL = "https://images.unsplash.com/photo-1724582586529-62622e50c0b3?crop=entropy&cs=srgb&fm=jpg&q=80&w=500";
const WHY_IMG = "https://images.unsplash.com/photo-1705326701287-346fc37a2c86?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200";
const BUYER_IMG = "https://images.unsplash.com/photo-1758227365187-016878604d94?crop=entropy&cs=srgb&fm=jpg&q=80&w=600";
const NRI_IMG = "https://images.unsplash.com/photo-1771244688590-1e481dba1b5a?crop=entropy&cs=srgb&fm=jpg&q=80&w=600";

const STAT_ICONS = [ClipboardList, HardHat, Users, MapPin];
const WHY_ICONS = [MapPin, Gem, RefreshCcw, LifeBuoy];
const PILLAR_ICONS = [Handshake, Building2, Eye, Clock];
const DEFAULT_PILLARS = [
  { title: "Trusted Partnerships", desc: "Long-term relationships with landowners and families" },
  { title: "Premium Build Quality", desc: "Branded materials and rigorous site supervision" },
  { title: "Transparent Process", desc: "Clear pricing, approvals and documentation" },
  { title: "On-time Delivery", desc: "Planned schedules with committed handover dates" },
];
const DEFAULT_POINTS = [
  "Premium residential & interior projects across Chennai",
  "Dedicated support for NRI investors",
  "Live progress updates through your private client portal",
];
const pickIcon = (item, fallbacks, i) => ICON_MAP[item?.icon] || fallbacks[i % fallbacks.length];
const DEFAULT_WHY = [
  { title: "Prime Locations", desc: "Well-connected, high-growth areas" },
  { title: "Superior Quality", desc: "Premium materials and expert craftsmanship" },
  { title: "Transparent Updates", desc: "Regular progress and clear communication" },
  { title: "End-to-End Support", desc: "From planning to handover and beyond" },
];

const Eyebrow = ({ children }) => (
  <p className="text-xs font-semibold uppercase tracking-[0.18em] mb-3 text-teal">{children}</p>
);

function SectionHead({ title, sub, link, linkText, testid }) {
  return (
    <div className="flex flex-wrap items-end justify-between gap-4 mb-6 sm:mb-8">
      <div>
        <h2 className="font-display text-2xl sm:text-4xl font-bold text-slate-900">{title}</h2>
        {sub && <p className="mt-2 text-sm sm:text-base text-slate-500">{sub}</p>}
      </div>
      {link && (
        <Link to={link} data-testid={testid} className="group inline-flex items-center gap-2 text-sm font-semibold text-navy hover:text-teal transition-colors">
          {linkText} <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
        </Link>
      )}
    </div>
  );
}

function CountUp({ value }) {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-40px" });
  const m = String(value || "").match(/^(\D*)(\d+(?:\.\d+)?)(.*)$/);
  const [n, setN] = useState(0);
  useEffect(() => {
    if (!m || !inView) { return; }
    const target = parseFloat(m[2]);
    const ctrl = animate(0, target, { duration: 1.6, ease: "easeOut", onUpdate: (v) => setN(v) });
    return () => ctrl.stop();
  }, [inView]); // eslint-disable-line react-hooks/exhaustive-deps
  if (!m) return <span ref={ref}>{value}</span>;
  const decimals = (m[2].split(".")[1] || "").length;
  return <span ref={ref}>{m[1]}{n.toFixed(decimals)}{m[3]}</span>;
}

function StatsBar({ stats }) {
  return (
    <section className="relative z-20 -mt-10 sm:-mt-12" data-testid="stats-section">
      <div className="mx-auto max-w-6xl px-4 sm:px-6">
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-px overflow-hidden rounded-2xl bg-slate-100 shadow-xl ring-1 ring-slate-100">
          {stats.slice(0, 4).map((s, i) => {
            const Icon = STAT_ICONS[i] || ShieldCheck;
            return (
              <Reveal key={i} delay={i * 0.08} className="flex items-center gap-3 sm:gap-4 bg-white px-4 py-5 sm:px-7 sm:py-6">
                <span className="flex h-12 w-12 sm:h-14 sm:w-14 shrink-0 items-center justify-center rounded-2xl bg-teal/10 text-teal">
                  <Icon className="h-6 w-6 sm:h-7 sm:w-7" strokeWidth={1.7} />
                </span>
                <div data-testid={`stat-${i}`}>
                  <div className="font-display text-xl sm:text-3xl font-bold text-navy leading-none"><CountUp value={s.value} /></div>
                  <div className="mt-1 text-xs sm:text-sm text-slate-500">{s.label}</div>
                </div>
              </Reveal>
            );
          })}
        </div>
      </div>
    </section>
  );
}

function WhoWeAre({ content }) {
  return (
    <section className="pt-14 pb-12 sm:pt-20 sm:pb-14" data-testid="who-we-are-section">
      <div className="mx-auto max-w-7xl px-4 sm:px-6">
        <div className="grid gap-10 lg:grid-cols-2 items-center">
          <Reveal>
            <Eyebrow>Who We Are</Eyebrow>
            <h2 className="font-display text-3xl sm:text-5xl font-bold text-slate-900 leading-[1.1]">
              {content?.intro_title || "Building Legacies, Not Just Structures."}
            </h2>
            <p className="mt-5 text-slate-600 leading-relaxed max-w-xl">
              {content?.intro_body || "VG Homes is a Chennai-based real estate developer delivering premium residential and interior projects."}
            </p>
            <ul className="mt-6 space-y-3" data-testid="intro-points">
              {(content?.intro_points?.length ? content.intro_points : DEFAULT_POINTS).map((pt, i) => (
                <li key={i} className="flex items-start gap-3 text-sm sm:text-base text-slate-700">
                  <CheckCircle2 className="mt-0.5 h-5 w-5 shrink-0 text-teal" /> {pt}
                </li>
              ))}
            </ul>
            <Link to="/services" data-testid="our-story-cta"
              className="mt-7 inline-flex items-center gap-2 rounded-full bg-navy px-7 py-3 text-sm font-semibold text-white hover:bg-navy-dark transition-colors">
              Our Story <ArrowRight className="h-4 w-4" />
            </Link>
          </Reveal>
          <Reveal delay={0.15} className="relative">
            <img src={content?.intro_image || INTRO} alt="VG Homes building" data-testid="intro-main-image" className="w-full h-[280px] sm:h-[400px] rounded-2xl object-cover shadow-xl lg:w-[82%]" />
            <div className="hidden sm:block absolute right-0 top-8 w-52 rounded-xl bg-white p-4 shadow-2xl ring-1 ring-slate-100">
              <p className="text-xs text-slate-500">Designed for</p>
              <p className="font-display text-lg font-semibold text-navy leading-tight">Modern Living</p>
              <img src={content?.intro_small_image || INTRO_SMALL} alt="Interior" data-testid="intro-small-image" className="mt-3 h-36 w-full rounded-lg object-cover" />
              <p className="mt-2 text-xs text-slate-500">Quality Spaces. Happier Lives.</p>
            </div>
          </Reveal>
        </div>
        <div className="mt-10 grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
          {(content?.pillars?.length ? content.pillars : DEFAULT_PILLARS).map((f, k) => {
            const Icon = pickIcon(f, PILLAR_ICONS, k);
            return (
              <Reveal key={k} delay={k * 0.06}>
                <div className="group flex h-full flex-col sm:flex-row items-start gap-3 sm:gap-4 rounded-2xl bg-white p-4 sm:p-5 ring-1 ring-slate-100 shadow-sm hover:shadow-lg hover:-translate-y-0.5 transition-[box-shadow,transform] duration-300" data-testid={`pillar-${k}`}>
                  <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-navy text-white group-hover:bg-teal transition-colors">
                    <Icon className="h-6 w-6" strokeWidth={1.7} />
                  </span>
                  <div>
                    <p className="font-semibold text-slate-900">{f.title}</p>
                    {f.desc && <p className="mt-1 text-sm text-slate-500 leading-snug">{f.desc}</p>}
                  </div>
                </div>
              </Reveal>
            );
          })}
        </div>
      </div>
    </section>
  );
}

function ProjectCard({ p }) {
  const chips = (p.highlights || []).slice(0, 2);
  return (
    <Link to={`/projects/${p.slug || p.id}`} data-testid={`home-project-${p.id}`}
      className="group flex sm:block overflow-hidden rounded-2xl bg-white ring-1 ring-slate-100 shadow-sm hover:shadow-xl hover:-translate-y-1 transition-[box-shadow,transform] duration-300">
      <div className="relative w-32 shrink-0 sm:w-auto h-auto sm:h-56 overflow-hidden">
        <img src={p.image} alt={p.name} className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105" />
        <div className="hidden sm:block absolute bottom-3 left-3"><StatusBadge status={p.status} /></div>
      </div>
      <div className="p-4 sm:p-5 min-w-0">
        <div className="sm:hidden mb-2"><StatusBadge status={p.status} /></div>
        <h3 className="font-display text-base sm:text-lg font-semibold text-slate-900 group-hover:text-navy truncate">{p.name}</h3>
        <p className="mt-1 text-xs sm:text-sm text-slate-500 truncate">{p.location}</p>
        {chips.length > 0 && (
          <div className="mt-3 flex flex-col sm:flex-row sm:flex-wrap gap-1.5 sm:gap-4 text-xs text-slate-600">
            {chips.map((h, i) => {
              const Icon = ICON_MAP[h.icon] || Building2;
              return <span key={i} className="inline-flex items-center gap-1.5"><Icon className="h-3.5 w-3.5 text-teal" /> {h.label}</span>;
            })}
          </div>
        )}
        <span className="mt-4 hidden sm:inline-flex items-center gap-1.5 text-sm font-semibold text-navy">
          View Project <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
        </span>
      </div>
    </Link>
  );
}

function FeaturedProjects({ projects }) {
  return (
    <section className="py-12 sm:py-16 bg-slate-50" data-testid="featured-projects-section">
      <div className="mx-auto max-w-7xl px-4 sm:px-6">
        <SectionHead title="Featured Projects" sub="Crafted with attention to detail, designed for modern living."
          link="/projects" linkText="View All Projects" testid="home-view-all-projects" />
        <div className="grid grid-cols-1 gap-4 sm:gap-6 md:grid-cols-3">
          {projects.map((p, i) => <Reveal key={p.id} delay={i * 0.1}><ProjectCard p={p} /></Reveal>)}
        </div>
      </div>
    </section>
  );
}

function WhyChoose({ content }) {
  const items = content?.why_choose?.length ? content.why_choose : DEFAULT_WHY;
  return (
    <section className="py-12 sm:py-16" data-testid="why-choose-section">
      <div className="mx-auto max-w-7xl px-4 sm:px-6">
        <div className="grid overflow-hidden rounded-3xl bg-navy lg:grid-cols-2 shadow-2xl">
          <div className="relative h-56 sm:h-80 lg:h-auto overflow-hidden">
            <img src={content?.why_image || WHY_IMG} alt="Luxury interior" className="absolute inset-0 h-full w-full object-cover" />
          </div>
          <div className="relative grain-overlay p-7 sm:p-12">
            <Reveal className="relative z-10">
              <h2 className="font-display text-2xl sm:text-4xl font-bold text-white">{content?.why_title || "Why Choose VG Homes?"}</h2>
              <p className="mt-3 text-sm sm:text-base text-white/75 max-w-lg">
                {content?.why_body || "We go beyond construction. We create spaces that enrich lives through quality, transparency and a client-first approach."}
              </p>
              <div className="mt-8 grid gap-5 sm:grid-cols-2">
                {items.map((w, i) => {
                  const Icon = pickIcon(w, WHY_ICONS, i);
                  return (
                    <div key={i} className="flex gap-4 rounded-xl bg-white/5 p-4 ring-1 ring-white/10" data-testid={`why-item-${i}`}>
                      <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-teal text-white">
                        <Icon className="h-6 w-6" strokeWidth={1.7} />
                      </span>
                      <div>
                        <p className="font-semibold text-white text-sm sm:text-base">{w.title}</p>
                        <p className="text-xs sm:text-sm text-white/65">{w.desc}</p>
                      </div>
                    </div>
                  );
                })}
              </div>
            </Reveal>
          </div>
        </div>
      </div>
    </section>
  );
}

function AudienceCard({ icon: Icon, title, desc, img, cta, testid }) {
  return (
    <Link to="/contact" data-testid={testid}
      className="group flex items-stretch overflow-hidden rounded-2xl bg-white ring-1 ring-slate-100 shadow-sm hover:shadow-xl transition-shadow">
      <div className="flex-1 p-5 sm:p-7">
        <span className="flex h-12 w-12 items-center justify-center rounded-xl bg-teal/10 text-teal"><Icon className="h-6 w-6" /></span>
        <h3 className="mt-4 font-display text-lg sm:text-xl font-semibold text-slate-900">{title}</h3>
        <p className="mt-2 text-xs sm:text-sm text-slate-500 leading-relaxed">{desc}</p>
        <span className="mt-4 inline-flex items-center gap-1.5 text-sm font-semibold text-navy group-hover:text-teal transition-colors">
          {cta} <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
        </span>
      </div>
      <div className="w-32 sm:w-48 shrink-0 overflow-hidden">
        <img src={img} alt={title} className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105" />
      </div>
    </Link>
  );
}

function Audiences({ content }) {
  return (
    <section className="pb-12 sm:pb-16" data-testid="audiences-section">
      <div className="mx-auto max-w-7xl px-4 sm:px-6">
        <SectionHead title="For Homebuyers & NRI Investors" sub="Tailored guidance. Personalised support. A seamless buying experience." />
        <div className="grid grid-cols-1 gap-5 md:grid-cols-2">
          <Reveal><AudienceCard icon={HomeIcon} title="For Homebuyers" img={content?.homebuyers_image || BUYER_IMG} cta="Explore for Homebuyers" testid="audience-homebuyers"
            desc={content?.homebuyers_desc || "Find your dream home with thoughtfully designed spaces in prime Chennai locations."} /></Reveal>
          <Reveal delay={0.1}><AudienceCard icon={Globe2} title="For NRI Investors" img={content?.nri_image || NRI_IMG} cta="Explore for NRI Investors" testid="audience-nri"
            desc={content?.nri_desc || "A trusted partner for secure, high-value investments in Chennai real estate."} /></Reveal>
        </div>
      </div>
    </section>
  );
}

function Testimonials({ items }) {
  if (!items.length) return null;
  return (
    <section className="py-12 sm:py-16 bg-slate-50" data-testid="testimonials-section">
      <div className="mx-auto max-w-7xl px-4 sm:px-6">
        <SectionHead title="Client Testimonials" sub="Real stories from happy homeowners and investors." />
        <div className="flex gap-5 overflow-x-auto snap-x snap-mandatory pb-2 md:grid md:grid-cols-3 md:overflow-visible">
          {items.map((t, i) => (
            <Reveal key={i} delay={i * 0.08} className="min-w-[85%] sm:min-w-[60%] md:min-w-0 snap-start">
              <div className="relative h-full rounded-2xl bg-white p-6 ring-1 ring-slate-100 shadow-sm" data-testid={`testimonial-${i}`}>
                <Quote className="absolute right-5 top-5 h-8 w-8 text-teal/15" />
                <p className="text-sm text-slate-600 leading-relaxed">“{t.quote}”</p>
                <div className="mt-4 flex gap-0.5">
                  {Array.from({ length: Math.min(5, t.rating || 5) }).map((_, k) => <Star key={k} className="h-4 w-4 fill-amber-400 text-amber-400" />)}
                </div>
                <div className="mt-4 flex items-center gap-3">
                  {t.photo ? (
                    <img src={t.photo} alt={t.name} className="h-11 w-11 rounded-full object-cover ring-2 ring-teal/30" data-testid={`testimonial-photo-${i}`} />
                  ) : (
                    <span className="flex h-11 w-11 items-center justify-center rounded-full bg-navy text-sm font-semibold text-white">
                      {(t.name || "?").replace(/^[A-Z]\.\s*/, "").charAt(0)}
                    </span>
                  )}
                  <div>
                    <p className="text-sm font-semibold text-slate-900">{t.name}</p>
                    <p className="text-xs text-slate-500">{t.role}</p>
                  </div>
                </div>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}

function CtaBand({ content }) {
  return (
    <section className="py-10 sm:py-14" data-testid="cta-section">
      <div className="mx-auto max-w-7xl px-4 sm:px-6">
        <Reveal className="relative overflow-hidden rounded-3xl bg-navy grain-overlay px-6 py-9 sm:px-12 sm:py-12">
          <div className="relative z-10 flex flex-col lg:flex-row lg:items-center lg:justify-between gap-6">
            <div>
              <h2 className="font-display text-2xl sm:text-4xl font-bold text-white">{content?.cta_title || "Let's Build Your Next Chapter"}</h2>
              <p className="mt-2 text-sm sm:text-base text-white/75">{content?.cta_body || "Speak with our advisory team — dedicated support for homebuyers and NRI investors."}</p>
            </div>
            <div className="flex flex-wrap gap-3">
              <Link to="/contact" data-testid="cta-talk-team" className="inline-flex items-center gap-2 rounded-full bg-teal px-6 py-3 text-sm font-semibold text-white hover:bg-teal-dark transition-colors">
                Talk to Our Team <ArrowRight className="h-4 w-4" />
              </Link>
              <a href={BRAND.whatsapp} target="_blank" rel="noreferrer" data-testid="cta-whatsapp"
                className="inline-flex items-center gap-2 rounded-full border border-white/40 px-6 py-3 text-sm font-semibold text-white hover:bg-white hover:text-navy transition-colors">
                <MessageCircle className="h-4 w-4" /> WhatsApp Us
              </a>
            </div>
          </div>
        </Reveal>
      </div>
    </section>
  );
}

export default function Home() {
  useSeo("home");
  const [content, setContent] = useState(null);
  const [allProjects, setAllProjects] = useState([]);
  const [testimonials, setTestimonials] = useState([]);
  const { scrollY } = useScroll();
  const heroY = useTransform(scrollY, [0, 600], [0, 160]);

  useEffect(() => {
    api.get("/content/home").then((r) => setContent(r.data || {})).catch(() => setContent({}));
    api.get("/projects").then((r) => setAllProjects(r.data)).catch(() => {});
    api.get("/content/testimonials").then((r) => setTestimonials(r.data?.items || [])).catch(() => {});
  }, []);

  const picked = (content?.featured_ids || []).map((id) => allProjects.find((p) => p.id === id)).filter(Boolean);
  const projects = [...picked, ...allProjects.filter((p) => !picked.includes(p))].slice(0, 3);

  const stats = content?.stats || [
    { value: "15+", label: "Years of Experience" },
    { value: "40+", label: "Projects Delivered" },
    { value: "100%", label: "On-time Handover" },
    { value: "Chennai", label: "Our Expertise" },
  ];

  return (
    <div>
      {/* HERO */}
      <section className="relative h-[92vh] min-h-[560px] overflow-hidden bg-navy-void" data-testid="hero-section">
        <motion.div style={{ y: heroY }} className="absolute inset-0">
          {content !== null && <img src={content?.hero_image || HERO} alt="VG Homes premium residence" data-testid="hero-image" className="h-[115%] w-full object-cover" />}
        </motion.div>
        <div className="absolute inset-0 bg-gradient-to-r from-navy-void/95 via-navy/75 to-navy/30" />
        <div className="relative z-10 mx-auto max-w-7xl px-4 sm:px-6 h-full flex flex-col justify-center">
          <motion.p initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}
            className="text-xs font-semibold uppercase tracking-[0.2em] text-teal mb-4">
            Real Estate Developer · Promoter · Property Management
          </motion.p>
          <motion.h1 initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, delay: 0.1 }}
            className="font-display text-4xl sm:text-5xl lg:text-6xl font-extrabold text-white leading-[1.08] max-w-3xl">
            {content?.hero_title || "Crafting High-Yield & Bespoke Living Spaces"}
          </motion.h1>
          <motion.p initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, delay: 0.25 }}
            className="mt-6 text-lg text-white/80 max-w-xl leading-relaxed">
            {content?.hero_subtitle || "Premium residences engineered for elite homeowners and NRIs."}
          </motion.p>
          <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, delay: 0.4 }}
            className="mt-9 flex flex-wrap gap-4">
            <Link to="/projects" data-testid="hero-projects-cta"
              className="inline-flex items-center gap-2 rounded-full bg-teal px-7 py-3.5 font-semibold text-white hover:bg-teal-dark transition-colors">
              Explore Projects <ArrowRight className="h-4 w-4" />
            </Link>
            <Link to="/contact" data-testid="hero-contact-cta"
              className="inline-flex items-center gap-2 rounded-full border border-white/40 px-7 py-3.5 font-semibold text-white hover:bg-white hover:text-navy transition-colors">
              Book a Consultation
            </Link>
          </motion.div>
        </div>
      </section>

      <StatsBar stats={stats} />
      <WhoWeAre content={content} />
      <FeaturedProjects projects={projects} />
      <WhyChoose content={content} />
      <Audiences content={content} />
      <Testimonials items={testimonials} />
      <CtaBand content={content} />
    </div>
  );
}
